import { useEffect, useRef, useState } from "react";
import { applyPageMetadata } from "../../lib/seo";
import {
  playSound,
  toggleSound,
  toggleBgm,
  startBgm,
  stopBgm,
} from "./tantanganSound";
import "./tantangan-terbuka.css";

const W = 640;
const H = 360;
const GROUND_Y = 286;
const ASSET_ROOT = "/games/tantangan-terbuka/";

export type FighterChar = "ren" | "boma";
export type GameMode = "pve" | "pvp";
export type Difficulty = "easy" | "normal" | "hard";
export type Arena = "padepokan" | "kuil-rembulan";

export type ActionState =
  | "idle"
  | "walk"
  | "jump"
  | "duck"
  | "light"
  | "heavy"
  | "special"
  | "up_light"      // Launcher
  | "down_light"    // Low Sweep
  | "forward_heavy" // Dash Lunge
  | "down_heavy"    // Ground Slam
  | "hurt"
  | "dizzy"         // Guard broken
  | "dead";

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

export interface FloatingText {
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
}

export interface Fighter {
  id: "p1" | "p2";
  char: FighterChar;
  name: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: 1 | -1;
  hp: number;
  maxHp: number;
  chipHp: number;
  ki: number;
  guard: number; // 0 to 100 Guard Meter
  state: ActionState;
  stateTimer: number;
  stateDuration: number;
  isGrounded: boolean;
  jumpsLeft: number; // 2 for Double Jump
  airFlipAngle: number;
  isBlocking: boolean;
  parryTimer: number;
  hasHit: boolean;
  combo: number;
  comboTimer: number;
  roundsWon: number;
  dashCooldown: number;
  invulnerable: number;
}

export interface TouchInputs {
  left: boolean;
  right: boolean;
  jump: boolean;
  duck: boolean;
  block: boolean;
  light: boolean;
  heavy: boolean;
  special: boolean;
  dash: boolean;
  launcherQuick: boolean;
  sweepQuick: boolean;
}

export interface GameState {
  phase: "menu" | "round_intro" | "fight" | "ko" | "round_end" | "match_over";
  round: number;
  roundTimer: number;
  announcement: { title: string; subtitle: string; timer: number };
  hitstop: number;
  slowmo: number;
  screenShake: number;
  particles: Particle[];
  texts: FloatingText[];
  p1: Fighter;
  p2: Fighter;
  mode: GameMode;
  difficulty: Difficulty;
  arena: Arena;
  winner: "p1" | "p2" | "draw" | null;
}

const CHAR_INFO = {
  ren: {
    name: "REN",
    title: "Pendekar Bayangan",
    desc: "Katana bulan sabit · Kecepatan kilat · Double Jump · Tebasan Bayangan",
    hp: 100,
    speed: 175,
    theme: "#45e8ff",
  },
  boma: {
    name: "BOMA",
    title: "Ksatria Bara",
    desc: "Tinju api lahar · Guard breaker · Double Jump · Tendangan Naga Bara",
    hp: 110,
    speed: 155,
    theme: "#ff7b2b",
  },
};

function createFighter(
  id: "p1" | "p2",
  char: FighterChar,
  x: number,
  facing: 1 | -1
): Fighter {
  const hp = CHAR_INFO[char].hp;
  return {
    id,
    char,
    name: CHAR_INFO[char].name,
    x,
    y: GROUND_Y,
    vx: 0,
    vy: 0,
    facing,
    hp,
    maxHp: hp,
    chipHp: hp,
    ki: 0,
    guard: 100,
    state: "idle",
    stateTimer: 0,
    stateDuration: 0,
    isGrounded: true,
    jumpsLeft: 2,
    airFlipAngle: 0,
    isBlocking: false,
    parryTimer: 0,
    hasHit: false,
    combo: 0,
    comboTimer: 0,
    roundsWon: 0,
    dashCooldown: 0,
    invulnerable: 0,
  };
}

function nextRound(g: GameState) {
  g.round++;
  g.roundTimer = 60;
  g.phase = "round_intro";
  g.announcement = {
    title: g.round === 3 ? "RONDE PENENTUAN" : `RONDE ${g.round}`,
    subtitle: "BERTARUNG!",
    timer: 1.8,
  };
  playSound("gong");
  startBgm();

  g.p1.x = 160;
  g.p1.y = GROUND_Y;
  g.p1.vx = 0;
  g.p1.vy = 0;
  g.p1.facing = 1;
  g.p1.hp = g.p1.maxHp;
  g.p1.chipHp = g.p1.maxHp;
  g.p1.guard = 100;
  g.p1.state = "idle";
  g.p1.stateTimer = 0;
  g.p1.combo = 0;
  g.p1.jumpsLeft = 2;

  g.p2.x = 480;
  g.p2.y = GROUND_Y;
  g.p2.vx = 0;
  g.p2.vy = 0;
  g.p2.facing = -1;
  g.p2.hp = g.p2.maxHp;
  g.p2.chipHp = g.p2.maxHp;
  g.p2.guard = 100;
  g.p2.state = "idle";
  g.p2.stateTimer = 0;
  g.p2.combo = 0;
  g.p2.jumpsLeft = 2;
}

function spawnBurst(
  g: GameState,
  x: number,
  y: number,
  color: string,
  count = 12,
  speed = 140
) {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const s = Math.random() * speed + 30;
    g.particles.push({
      x,
      y,
      vx: Math.cos(angle) * s,
      vy: Math.sin(angle) * s - 20,
      life: Math.random() * 0.35 + 0.15,
      maxLife: 0.4,
      color,
      size: Math.random() * 3 + 2,
    });
  }
}

function addFloatingText(
  g: GameState,
  x: number,
  y: number,
  text: string,
  color = "#ffea78"
) {
  g.texts.push({ x, y, text, color, life: 0.75 });
}

function applyDamage(
  g: GameState,
  attacker: Fighter,
  target: Fighter,
  amount: number,
  moveType: ActionState
) {
  // Check Parry window
  if (target.parryTimer > 0) {
    playSound("parry");
    spawnBurst(g, (attacker.x + target.x) / 2, attacker.y - 45, "#ffd700", 22, 240);
    addFloatingText(g, target.x, target.y - 75, "PARRY!", "#ffd700");

    attacker.state = "hurt";
    attacker.stateTimer = 0;
    attacker.stateDuration = 0.48;
    attacker.vx = -attacker.facing * 120;

    target.ki = Math.min(100, target.ki + 35);
    g.hitstop = 0.15;
    g.screenShake = 7;
    return;
  }

  // Check Guard / Block
  const isFacingAttacker = Math.sign(attacker.x - target.x) === target.facing;
  if (target.isBlocking && isFacingAttacker) {
    // Low sweeps hit standing guard!
    if (moveType === "down_light" && target.state !== "duck") {
      addFloatingText(g, target.x, target.y - 65, "LOW HIT!", "#ff8844");
    } else {
      // Guarded!
      target.guard = Math.max(0, target.guard - (moveType === "heavy" ? 30 : 15));
      if (target.guard <= 0) {
        // GUARD BREAK!
        playSound("guard_break");
        addFloatingText(g, target.x, target.y - 85, "GUARD BREAK!", "#ff3333");
        target.state = "dizzy";
        target.stateTimer = 0;
        target.stateDuration = 0.8;
        target.guard = 40;
        g.screenShake = 12;
        g.hitstop = 0.18;
        spawnBurst(g, target.x, target.y - 45, "#ff3355", 20, 180);
        return;
      }

      playSound("block");
      const chip = Math.max(2, Math.round(amount * 0.16));
      target.hp = Math.max(0, target.hp - chip);
      target.vx = attacker.facing * (moveType === "heavy" ? 100 : 50);
      spawnBurst(g, (attacker.x + target.x) / 2, attacker.y - 45, "#7fb2ff", 9, 95);
      addFloatingText(g, target.x, target.y - 75, "GUARD", "#95cbff");

      attacker.ki = Math.min(100, attacker.ki + 6);
      target.ki = Math.min(100, target.ki + 10);
      g.hitstop = 0.05;
      return;
    }
  }

  // Counter Hit detection (if target was winding up an attack)
  const isCounter =
    target.state === "light" ||
    target.state === "heavy" ||
    target.state === "up_light" ||
    target.state === "down_light";

  const finalDamage = isCounter ? Math.round(amount * 1.3) : amount;
  if (isCounter) {
    playSound("counter_hit");
    addFloatingText(g, attacker.x, attacker.y - 95, "COUNTER HIT!", "#ff4466");
  }

  // Clean Hit
  target.hp = Math.max(0, target.hp - finalDamage);
  attacker.combo++;
  attacker.comboTimer = 1.6;

  // Sound & Screen Shake based on move
  if (moveType === "special") {
    playSound("heavy");
    g.screenShake = 16;
    g.hitstop = 0.22;
  } else if (moveType === "up_light") {
    playSound("launcher");
    addFloatingText(g, target.x, target.y - 85, "RISING LAUNCHER!", "#44eeff");
    g.screenShake = 8;
    g.hitstop = 0.1;
  } else if (moveType === "down_light") {
    playSound("sweep");
    addFloatingText(g, target.x, target.y - 75, "LOW SWEEP!", "#ffd244");
    g.screenShake = 5;
    g.hitstop = 0.08;
  } else if (moveType === "down_heavy") {
    playSound("ground_slam");
    addFloatingText(g, target.x, target.y - 85, "GROUND SLAM!", "#ff5533");
    g.screenShake = 12;
    g.hitstop = 0.14;
  } else if (moveType === "forward_heavy" || moveType === "heavy") {
    playSound("heavy");
    g.screenShake = 10;
    g.hitstop = 0.12;
  } else {
    playSound("combo_hit", 1.0 + attacker.combo * 0.15);
    g.screenShake = 4;
    g.hitstop = 0.06;
  }

  // Ki gains
  attacker.ki = Math.min(100, attacker.ki + (moveType === "special" ? 0 : 16));
  target.ki = Math.min(100, target.ki + 12);

  // Particle burst
  const sparkColor = attacker.char === "ren" ? "#4ff3ff" : "#ff7f24";
  spawnBurst(g, target.x, target.y - 45, sparkColor, 14, 160);

  // Physics reaction & air juggle
  target.state = "hurt";
  target.stateTimer = 0;
  target.stateDuration = moveType === "special" ? 0.6 : 0.32;

  if (moveType === "up_light") {
    // Launch high into the air
    target.vy = -420;
    target.vx = attacker.facing * 80;
    target.isGrounded = false;
  } else if (moveType === "down_heavy") {
    // Slam to ground
    target.vy = 400;
    target.vx = attacker.facing * 90;
  } else if (moveType === "down_light") {
    // Trip sweep
    target.vy = -160;
    target.vx = attacker.facing * 140;
    target.isGrounded = false;
  } else {
    target.vx = attacker.facing * (moveType === "special" ? 310 : 170);
    if (!target.isGrounded) {
      // Air Juggle!
      target.vy = -180;
      addFloatingText(g, target.x, target.y - 85, "AIR JUGGLE!", "#88ffff");
    }
  }

  // Check K.O.
  if (target.hp <= 0) {
    target.state = "dead";
    g.phase = "ko";
    g.slowmo = 0.25;
    g.screenShake = 20;
    stopBgm();
    playSound("ko");
    g.announcement = { title: "K.O.!", subtitle: `${attacker.name} MENANG!`, timer: 2.2 };
    attacker.roundsWon++;

    setTimeout(() => {
      g.slowmo = 1;
      if (attacker.roundsWon >= 2) {
        g.phase = "match_over";
        g.winner = attacker.id;
        playSound("victory");
      } else {
        nextRound(g);
      }
    }, 2200);
  }
}

function updateFighter(
  f: Fighter,
  opponent: Fighter,
  dt: number,
  input: {
    left: boolean;
    right: boolean;
    jump: boolean;
    duck: boolean;
    block: boolean;
    light: boolean;
    heavy: boolean;
    special: boolean;
    dash: boolean;
    launcherQuick: boolean;
    sweepQuick: boolean;
  },
  g: GameState
) {
  // Regenerate Guard when not blocking
  if (!f.isBlocking && f.guard < 100) {
    f.guard = Math.min(100, f.guard + dt * 15);
  }

  if (f.dashCooldown > 0) f.dashCooldown -= dt;
  if (f.parryTimer > 0) f.parryTimer -= dt;
  if (f.invulnerable > 0) f.invulnerable -= dt;
  if (f.comboTimer > 0) {
    f.comboTimer -= dt;
    if (f.comboTimer <= 0) f.combo = 0;
  }

  if (f.chipHp > f.hp) {
    f.chipHp = Math.max(f.hp, f.chipHp - dt * 45);
  }

  f.stateTimer += dt;

  // Face opponent unless in attack or dead
  const isAttacking = [
    "light",
    "heavy",
    "special",
    "up_light",
    "down_light",
    "forward_heavy",
    "down_heavy",
  ].includes(f.state);

  if (!isAttacking && f.state !== "dead") {
    f.facing = f.x < opponent.x ? 1 : -1;
  }

  // State transitions
  if (isAttacking && f.stateTimer >= f.stateDuration) {
    f.state = "idle";
  } else if ((f.state === "hurt" || f.state === "dizzy") && f.stateTimer >= f.stateDuration) {
    f.state = "idle";
  }

  if (f.state === "hurt" || f.state === "dizzy" || f.state === "dead" || g.phase !== "fight") {
    if (!f.isGrounded) {
      f.vy += 800 * dt;
      f.y += f.vy * dt;
      if (f.y < 85) {
        f.y = 85;
        f.vy = Math.max(0, f.vy);
      }
      if (f.y >= GROUND_Y) {
        f.y = GROUND_Y;
        f.vy = 0;
        f.isGrounded = true;
        f.jumpsLeft = 2;
      }
    }
    f.vx *= Math.pow(0.01, dt);
    f.x = Math.max(56, Math.min(W - 56, f.x + f.vx * dt));
    return;
  }

  // Blocking / Parry
  f.isBlocking = input.block;
  if (input.block && f.parryTimer <= 0 && f.state === "idle") {
    f.parryTimer = 0.18;
  }

  // Dash with Dash Cancel from Light Attack!
  const canDash =
    f.dashCooldown <= 0 &&
    f.state !== "special" &&
    (f.state === "idle" || f.state === "walk" || (f.state === "light" && f.hasHit));

  if (input.dash && canDash) {
    f.dashCooldown = 0.9;
    f.invulnerable = 0.22;
    f.state = "idle";
    const dashDir = input.left ? -1 : input.right ? 1 : f.facing;
    f.vx = dashDir * 420;
    playSound("dash");
    spawnBurst(g, f.x, f.y - 20, "#e0d8ff", 8, 80);
  }

  // Double Jump Mechanics
  if (input.jump && f.jumpsLeft > 0) {
    if (f.isGrounded) {
      f.vy = -380;
      f.isGrounded = false;
      f.jumpsLeft = 1;
      f.state = "jump";
      playSound("dash");
      spawnBurst(g, f.x, f.y, "#9288ab", 6, 60);
    } else if (f.jumpsLeft === 1) {
      // DOUBLE JUMP!
      f.vy = -360;
      f.jumpsLeft = 0;
      f.airFlipAngle = Math.PI * 2;
      playSound("double_jump");
      addFloatingText(g, f.x, f.y - 50, "DOUBLE JUMP!", "#88ffff");
      spawnBurst(g, f.x, f.y, "#44eeff", 10, 110);
    }
  }

  // Air flip angle decay
  if (f.airFlipAngle > 0) {
    f.airFlipAngle = Math.max(0, f.airFlipAngle - dt * 14);
  }

  // Command Attacks & Combos
  const canAttack =
    (f.isGrounded || !f.isGrounded) &&
    (f.state === "idle" || f.state === "walk" || f.state === "jump" || (f.state === "light" && f.hasHit));

  if (canAttack) {
    const isPressingForward = (input.right && f.facing === 1) || (input.left && f.facing === -1);

    if (input.special && f.ki >= 100) {
      // Special Move
      f.ki = 0;
      f.state = "special";
      f.stateTimer = 0;
      f.stateDuration = 0.8;
      f.hasHit = false;
      f.vx = f.facing * 360;
      playSound("special");
      spawnBurst(g, f.x, f.y - 45, f.char === "ren" ? "#44eeff" : "#ff6600", 22, 220);
      addFloatingText(
        g,
        f.x,
        f.y - 85,
        f.char === "ren" ? "JURUS BULAN SABIT!" : "JURUS NAGA BARA!",
        f.char === "ren" ? "#44eeff" : "#ff6600"
      );
    } else if (input.launcherQuick || (input.jump && input.light)) {
      // UP + LIGHT: Rising Launcher
      f.state = "up_light";
      f.stateTimer = 0;
      f.stateDuration = 0.44;
      f.hasHit = false;
      f.vy = -260; // Leaps upward while launching
      f.isGrounded = false;
    } else if (input.sweepQuick || (input.duck && input.light)) {
      // DOWN + LIGHT: Low Sweep
      f.state = "down_light";
      f.stateTimer = 0;
      f.stateDuration = 0.38;
      f.hasHit = false;
      f.vx = f.facing * 140;
    } else if (input.duck && input.heavy) {
      // DOWN + HEAVY: Ground Slam
      f.state = "down_heavy";
      f.stateTimer = 0;
      f.stateDuration = 0.48;
      f.hasHit = false;
      f.vx = f.facing * 70;
    } else if (isPressingForward && input.heavy) {
      // FORWARD + HEAVY: Dash Lunge Bash
      f.state = "forward_heavy";
      f.stateTimer = 0;
      f.stateDuration = 0.5;
      f.hasHit = false;
      f.vx = f.facing * 240;
    } else if (input.heavy) {
      // Neutral Heavy
      f.state = "heavy";
      f.stateTimer = 0;
      f.stateDuration = 0.46;
      f.hasHit = false;
      f.vx = f.facing * 120;
    } else if (input.light) {
      // Neutral Light
      f.state = "light";
      f.stateTimer = 0;
      f.stateDuration = 0.28;
      f.hasHit = false;
      f.vx = f.facing * 85;
    }
  }

  // Strike collision detection
  if (!f.hasHit && isAttacking) {
    const isSpecial = f.state === "special";
    const strikeDelay = isSpecial ? 0.16 : 0.08;

    if (f.stateTimer >= strikeDelay) {
      const reach = isSpecial ? 120 : f.state === "forward_heavy" ? 95 : 75;
      const dist = Math.abs(f.x - opponent.x);
      const inFront = (opponent.x - f.x) * f.facing > 0;

      if (dist <= reach && inFront && opponent.state !== "dead") {
        f.hasHit = true;
        let dmg = 12;
        if (f.state === "special") dmg = 48;
        else if (f.state === "forward_heavy" || f.state === "down_heavy") dmg = 26;
        else if (f.state === "heavy") dmg = 22;
        else if (f.state === "up_light") dmg = 18;
        else if (f.state === "down_light") dmg = 14;

        applyDamage(g, f, opponent, dmg, f.state);
      }
    }
  }

  // Movement & Crouch
  if (f.state === "idle" || f.state === "walk") {
    let moveDir = 0;
    if (input.left) moveDir -= 1;
    if (input.right) moveDir += 1;

    if (moveDir !== 0 && !input.duck && !input.block) {
      const speed = CHAR_INFO[f.char].speed;
      f.vx = moveDir * speed;
      f.state = "walk";
    } else {
      f.vx = 0;
      f.state = input.duck ? "duck" : "idle";
    }
  }

  // Gravity
  if (!f.isGrounded) {
    f.vy += 820 * dt;
    f.y += f.vy * dt;
    if (f.y < 85) {
      f.y = 85;
      f.vy = Math.max(0, f.vy);
    }
    if (f.y >= GROUND_Y) {
      f.y = GROUND_Y;
      f.vy = 0;
      f.isGrounded = true;
      f.jumpsLeft = 2;
      f.airFlipAngle = 0;
      if (f.state === "jump") f.state = "idle";
    }
  }

  f.x = Math.max(56, Math.min(W - 56, f.x + f.vx * dt));

  // Push fighters apart if colliding
  const overlap = 42 - Math.abs(f.x - opponent.x);
  if (overlap > 0 && Math.abs(f.y - opponent.y) < 30) {
    const push = overlap / 2;
    if (f.x < opponent.x) {
      f.x = Math.max(56, f.x - push);
      opponent.x = Math.min(W - 56, opponent.x + push);
    } else {
      f.x = Math.min(W - 56, f.x + push);
      opponent.x = Math.max(56, opponent.x - push);
    }
  }
}

// Responsive AI
function getAiInputs(cpu: Fighter, player: Fighter, diff: Difficulty) {
  const dist = Math.abs(cpu.x - player.x);
  const playerAttacking = [
    "light",
    "heavy",
    "special",
    "up_light",
    "down_light",
    "forward_heavy",
  ].includes(player.state);

  const blockChance = diff === "hard" ? 0.78 : diff === "normal" ? 0.52 : 0.22;
  const attackFreq = diff === "hard" ? 0.09 : diff === "normal" ? 0.06 : 0.035;

  const inputs = {
    left: false,
    right: false,
    jump: false,
    duck: false,
    block: false,
    light: false,
    heavy: false,
    special: false,
    dash: false,
    launcherQuick: false,
    sweepQuick: false,
  };

  if (playerAttacking && dist < 110) {
    if (Math.random() < blockChance) {
      inputs.block = true;
      if (player.state === "down_light") inputs.duck = true; // Crouch block
    }
  }

  if (dist > 95) {
    if (cpu.x < player.x) inputs.right = true;
    else inputs.left = true;

    if (dist > 140 && Math.random() < 0.03) inputs.dash = true;
    if (Math.random() < 0.015 && cpu.isGrounded) inputs.jump = true;
  } else {
    // In close range
    if (cpu.ki >= 100 && Math.random() < 0.3) {
      inputs.special = true;
    } else if (Math.random() < attackFreq) {
      const roll = Math.random();
      if (roll < 0.35) inputs.light = true;
      else if (roll < 0.6) inputs.launcherQuick = true;
      else if (roll < 0.8) inputs.sweepQuick = true;
      else inputs.heavy = true;
    } else if (Math.random() < 0.04) {
      // Back dash
      inputs.dash = true;
      if (cpu.x < player.x) inputs.left = true;
      else inputs.right = true;
    }
  }

  // Double jump air chase
  if (!cpu.isGrounded && cpu.jumpsLeft === 1 && !player.isGrounded && Math.random() < 0.08) {
    inputs.jump = true;
  }

  return inputs;
}

function updateGame(
  g: GameState,
  dt: number,
  keys: Set<string>,
  touch: TouchInputs
) {
  if (g.screenShake > 0) {
    g.screenShake = Math.max(0, g.screenShake - dt * 25);
  }

  if (g.announcement.timer > 0) {
    g.announcement.timer -= dt;
    if (g.announcement.timer <= 0 && g.phase === "round_intro") {
      g.phase = "fight";
    }
  }

  if (g.phase === "fight") {
    g.roundTimer -= dt;
    if (g.roundTimer <= 0) {
      g.roundTimer = 0;
      g.phase = "ko";
      stopBgm();
      if (g.p1.hp > g.p2.hp) {
        g.p1.roundsWon++;
        g.announcement = { title: "WAKTU HABIS!", subtitle: `${g.p1.name} MENANG HP`, timer: 2 };
      } else if (g.p2.hp > g.p1.hp) {
        g.p2.roundsWon++;
        g.announcement = { title: "WAKTU HABIS!", subtitle: `${g.p2.name} MENANG HP`, timer: 2 };
      } else {
        g.announcement = { title: "WAKTU HABIS!", subtitle: "SERI!", timer: 2 };
      }
      setTimeout(() => {
        if (g.p1.roundsWon >= 2 || g.p2.roundsWon >= 2) {
          g.phase = "match_over";
          g.winner = g.p1.roundsWon >= 2 ? "p1" : "p2";
          playSound("victory");
        } else {
          nextRound(g);
        }
      }, 2000);
    }
  }

  g.particles = g.particles.filter((p) => p.life > 0);
  for (const p of g.particles) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 220 * dt;
    p.life -= dt;
  }

  g.texts = g.texts.filter((t) => t.life > 0);
  for (const t of g.texts) {
    t.y -= 30 * dt;
    t.life -= dt;
  }

  const p1Inputs = {
    left: keys.has("a") || touch.left,
    right: keys.has("d") || touch.right,
    jump: keys.has("w") || touch.jump,
    duck: keys.has("s") || touch.duck,
    block: keys.has("s") || touch.block,
    light: keys.has("j") || touch.light,
    heavy: keys.has("k") || touch.heavy,
    special: keys.has("l") || touch.special,
    dash: keys.has("shift") || keys.has(" ") || touch.dash,
    launcherQuick: touch.launcherQuick,
    sweepQuick: touch.sweepQuick,
  };

  let p2Inputs;
  if (g.mode === "pve") {
    p2Inputs = getAiInputs(g.p2, g.p1, g.difficulty);
  } else {
    p2Inputs = {
      left: keys.has("arrowleft"),
      right: keys.has("arrowright"),
      jump: keys.has("arrowup"),
      duck: keys.has("arrowdown"),
      block: keys.has("arrowdown"),
      light: keys.has("1") || keys.has("numpad1"),
      heavy: keys.has("2") || keys.has("numpad2"),
      special: keys.has("3") || keys.has("numpad3"),
      dash: keys.has("0") || keys.has("enter"),
      launcherQuick: false,
      sweepQuick: false,
    };
  }

  updateFighter(g.p1, g.p2, dt, p1Inputs, g);
  updateFighter(g.p2, g.p1, dt, p2Inputs, g);
}

function drawFighterShadow(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const heightAboveGround = Math.max(0, GROUND_Y - y);
  const scale = Math.max(0.35, 1 - heightAboveGround / 220);
  ctx.save();
  ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
  ctx.beginPath();
  ctx.ellipse(Math.round(x), GROUND_Y, 26 * scale, 6 * scale, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawSpriteFrame(
  ctx: CanvasRenderingContext2D,
  sheet: HTMLImageElement | undefined,
  frame: number,
  totalFrames: number,
  f: Fighter
) {
  ctx.save();

  const isRen = f.char === "ren";
  const flip = f.facing === -1;
  const size = 128;
  const centerY = f.y - size / 2;

  // 1. Move to center of character body for rotation & scaling
  ctx.translate(Math.round(f.x), Math.round(centerY));

  // 2. Horizontal Facing Flip
  if (flip) ctx.scale(-1, 1);

  // 3. Double Jump Air Flip (rotates around center of mass)
  if (f.airFlipAngle > 0) {
    ctx.rotate(f.airFlipAngle);
  }

  // 4. Dead State / K.O. Defeat Pose
  if (f.state === "dead") {
    ctx.translate(0, 16); // Lowered onto knees on the arena floor
    ctx.rotate(f.facing * -0.08); // Head bowed back from impact
    ctx.filter = "brightness(0.8) contrast(1.1)"; // Defeated silhouette tone
  }

  // 5. Ducking Crouch Offset
  if (f.state === "duck") {
    ctx.translate(0, 16);
  }

  // 6. Dizzy Stun Wobble (Guard Break)
  if (f.state === "dizzy") {
    ctx.rotate(Math.sin(f.stateTimer * 16) * 0.12);
  }

  // 7. Hit Flash (Visual Impact Feedback)
  if (f.state === "hurt" && f.stateTimer < 0.08) {
    ctx.filter = "brightness(2.2)";
  }

  // 8. Draw Sprite Cell
  if (sheet && sheet.complete && sheet.naturalWidth) {
    const cellW = 128;
    const cellH = 128;
    const sx = Math.max(0, Math.min(totalFrames - 1, frame)) * cellW;
    ctx.drawImage(
      sheet,
      sx,
      0,
      cellW,
      cellH,
      -size / 2,
      -size / 2,
      size,
      size
    );
  } else {
    // Fallback crisp rectangle
    ctx.fillStyle = isRen ? "#36b2f0" : "#d84f2c";
    ctx.fillRect(-16, -size / 2, 32, size);
  }

  ctx.restore();

  // 9. Extra VFX: Dizzy Stars above head
  if (f.state === "dizzy") {
    ctx.save();
    ctx.translate(Math.round(f.x), Math.round(f.y - size - 8));
    ctx.fillStyle = "#ffe600";
    for (let i = 0; i < 3; i++) {
      const starAngle = f.stateTimer * 6 + (i * Math.PI * 2) / 3;
      const starX = Math.cos(starAngle) * 16;
      const starY = Math.sin(starAngle) * 5;
      ctx.fillRect(Math.round(starX) - 2, Math.round(starY) - 2, 5, 5);
    }
    ctx.restore();
  }
}

function renderGame(
  ctx: CanvasRenderingContext2D,
  g: GameState,
  images: Record<string, HTMLImageElement>
) {
  ctx.save();
  ctx.imageSmoothingEnabled = false;

  if (g.screenShake > 0) {
    const sx = (Math.random() - 0.5) * g.screenShake;
    const sy = (Math.random() - 0.5) * g.screenShake;
    ctx.translate(Math.round(sx), Math.round(sy));
  }

  const bgKey = `${g.arena}.webp`;
  const bgImg = images[bgKey];
  if (bgImg && bgImg.complete && bgImg.naturalWidth) {
    ctx.drawImage(bgImg, 0, 0, W, H);
  } else {
    ctx.fillStyle = g.arena === "padepokan" ? "#1e131d" : "#0c1322";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#2d2038";
    ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);
  }

  const grad = ctx.createLinearGradient(0, GROUND_Y, 0, H);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.45)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);

  const fighters = [g.p1, g.p2].sort((a, b) => a.y - b.y);

  // 1. Draw floor shadows for all fighters first
  for (const f of fighters) {
    drawFighterShadow(ctx, f.x, f.y);
  }

  // 2. Draw fighters
  for (const f of fighters) {
    const isRen = f.char === "ren";
    const prefix = isRen ? "ren" : "boma";

    let sheetName = `${prefix}-idle-sheet.png`;
    let totalFrames = 6;
    let frame = Math.floor(f.stateTimer * 7) % totalFrames;

    if (f.state === "dead") {
      sheetName = `${prefix}-hurt-sheet.png`;
      totalFrames = 4;
      frame = 1; // Down on one knee defeated posture
    } else if (f.state === "light" || f.state === "up_light" || f.state === "down_light") {
      sheetName = `${prefix}-attack-sheet.png`;
      totalFrames = 6;
      frame = Math.min(
        totalFrames - 1,
        Math.floor((f.stateTimer / f.stateDuration) * totalFrames)
      );
    } else if (f.state === "heavy" || f.state === "special" || f.state === "forward_heavy" || f.state === "down_heavy") {
      sheetName = `${prefix}-heavy-sheet.png`;
      totalFrames = 7;
      frame = Math.min(
        totalFrames - 1,
        Math.floor((f.stateTimer / f.stateDuration) * totalFrames)
      );
    } else if (f.state === "hurt" || f.state === "dizzy") {
      sheetName = `${prefix}-hurt-sheet.png`;
      totalFrames = 4;
      frame = Math.min(
        totalFrames - 1,
        Math.floor((f.stateTimer / Math.max(0.1, f.stateDuration)) * totalFrames)
      );
    } else if (f.state === "duck") {
      sheetName = `${prefix}-hurt-sheet.png`;
      totalFrames = 4;
      frame = 1; // Crouched guard frame
    } else if (f.state === "jump") {
      sheetName = `${prefix}-heavy-sheet.png`;
      totalFrames = 7;
      frame = 1; // Airborne jump pose
    } else if (f.state === "walk") {
      sheetName = `${prefix}-idle-sheet.png`;
      totalFrames = 6;
      frame = Math.floor(f.stateTimer * 12) % totalFrames;
    }

    if (f.state === "special") {
      ctx.save();
      ctx.globalAlpha = 0.4 + Math.sin(f.stateTimer * 20) * 0.2;
      ctx.strokeStyle = isRen ? "#4ff3ff" : "#ff7020";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(f.x, f.y - 45, 55, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    if (f.isBlocking) {
      ctx.save();
      ctx.strokeStyle = "#80b8ff";
      ctx.lineWidth = 3;
      ctx.beginPath();
      // Shield arc always opens outwards toward opponent in front
      const startAngle = f.facing === 1 ? -Math.PI * 0.4 : Math.PI * 0.6;
      const endAngle = f.facing === 1 ? Math.PI * 0.4 : Math.PI * 1.4;
      ctx.arc(f.x + f.facing * 18, f.y - 45, 34, startAngle, endAngle);
      ctx.stroke();
      ctx.restore();
    }

    drawSpriteFrame(
      ctx,
      images[sheetName],
      frame,
      totalFrames,
      f
    );
  }

  for (const p of g.particles) {
    ctx.save();
    const progress = Math.max(0, Math.min(1, p.life / p.maxLife));
    ctx.globalAlpha = progress;
    ctx.fillStyle = p.color;
    const pSize = Math.max(1.5, p.size * (0.4 + progress * 0.6));
    ctx.fillRect(Math.round(p.x), Math.round(p.y), pSize, pSize);
    ctx.restore();
  }

  for (const t of g.texts) {
    ctx.save();
    ctx.fillStyle = t.color;
    ctx.font = "900 13px 'Courier New', monospace";
    ctx.textAlign = "center";
    ctx.shadowColor = "#000000";
    ctx.shadowBlur = 4;
    ctx.fillText(t.text, Math.round(t.x), Math.round(t.y));
    ctx.restore();
  }

  ctx.restore();
}

export function TantanganTerbukaPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [soundOn, setSoundOn] = useState(true);
  const [bgmOn, setBgmOn] = useState(true);

  const [inMenu, setInMenu] = useState(true);
  const [selectedP1, setSelectedP1] = useState<FighterChar>("ren");
  const [selectedP2, setSelectedP2] = useState<FighterChar>("boma");
  const [gameMode, setGameMode] = useState<GameMode>("pve");
  const [difficulty, setDifficulty] = useState<Difficulty>("normal");
  const [arena, setArena] = useState<Arena>("padepokan");

  const [hudState, setHudState] = useState<{
    p1Hp: number;
    p1MaxHp: number;
    p1ChipHp: number;
    p1Ki: number;
    p1Guard: number;
    p1Rounds: number;
    p1Combo: number;
    p2Hp: number;
    p2MaxHp: number;
    p2ChipHp: number;
    p2Ki: number;
    p2Guard: number;
    p2Rounds: number;
    p2Combo: number;
    timer: number;
    round: number;
    announcement: { title: string; subtitle: string; visible: boolean };
    matchOver: boolean;
    winnerName: string;
  } | null>(null);

  const gameRef = useRef<GameState | null>(null);
  const keysRef = useRef<Set<string>>(new Set());
  const imagesRef = useRef<Record<string, HTMLImageElement>>({});

  const touchInputsRef = useRef<TouchInputs>({
    left: false,
    right: false,
    jump: false,
    duck: false,
    block: false,
    light: false,
    heavy: false,
    special: false,
    dash: false,
    launcherQuick: false,
    sweepQuick: false,
  });

  const startMatch = () => {
    playSound("gong");
    startBgm();
    const p1 = createFighter("p1", selectedP1, 160, 1);
    const p2 = createFighter(
      "p2",
      gameMode === "pve" ? (selectedP1 === "ren" ? "boma" : "ren") : selectedP2,
      480,
      -1
    );

    gameRef.current = {
      phase: "round_intro",
      round: 1,
      roundTimer: 60,
      announcement: { title: "RONDE 1", subtitle: "SIAP... BERTARUNG!", timer: 1.8 },
      hitstop: 0,
      slowmo: 1,
      screenShake: 0,
      particles: [],
      texts: [],
      p1,
      p2,
      mode: gameMode,
      difficulty,
      arena,
      winner: null,
    };

    setInMenu(false);
  };

  useEffect(() => {
    applyPageMetadata({
      title: "Tantangan Terbuka — Pixel Duel 1v1 | Steria House",
      description:
        "Game pertarungan duel pixel 1 lawan 1 retro arcade. Dilengkapi kombo tombol, double jump, tangkis, dan BGM 16-bit asli.",
      canonicalPath: "/games/tantangan-terbuka",
    });

    const artList = [
      "padepokan.webp",
      "kuil-rembulan.webp",
      "ren-idle-sheet.png",
      "ren-attack-sheet.png",
      "ren-heavy-sheet.png",
      "ren-hurt-sheet.png",
      "boma-idle-sheet.png",
      "boma-attack-sheet.png",
      "boma-heavy-sheet.png",
      "boma-hurt-sheet.png",
    ];

    artList.forEach((file) => {
      const img = new Image();
      img.src = ASSET_ROOT + file;
      imagesRef.current[file] = img;
    });

    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (
        ["arrowup", "arrowdown", "arrowleft", "arrowright", " ", "w", "s"].includes(
          key
        )
      ) {
        e.preventDefault();
      }
      keysRef.current.add(key);
    };

    const onKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.key.toLowerCase());
    };

    const onBlur = () => {
      keysRef.current.clear();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);

    let animId = 0;
    let lastTime = performance.now();
    let hudSyncTimer = 0;

    const tick = (now: number) => {
      let dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      const g = gameRef.current;
      if (g) {
        dt *= g.slowmo;

        if (g.hitstop > 0) {
          g.hitstop -= dt;
        } else {
          updateGame(g, dt, keysRef.current, touchInputsRef.current);
        }

        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          renderGame(ctx, g, imagesRef.current);
        }

        hudSyncTimer += dt;
        if (hudSyncTimer > 0.05) {
          hudSyncTimer = 0;
          setHudState({
            p1Hp: g.p1.hp,
            p1MaxHp: g.p1.maxHp,
            p1ChipHp: g.p1.chipHp,
            p1Ki: g.p1.ki,
            p1Guard: g.p1.guard,
            p1Rounds: g.p1.roundsWon,
            p1Combo: g.p1.combo,
            p2Hp: g.p2.hp,
            p2MaxHp: g.p2.maxHp,
            p2ChipHp: g.p2.chipHp,
            p2Ki: g.p2.ki,
            p2Guard: g.p2.guard,
            p2Rounds: g.p2.roundsWon,
            p2Combo: g.p2.combo,
            timer: Math.ceil(g.roundTimer),
            round: g.round,
            announcement: {
              title: g.announcement.title,
              subtitle: g.announcement.subtitle,
              visible: g.announcement.timer > 0,
            },
            matchOver: g.phase === "match_over",
            winnerName:
              g.winner === "p1"
                ? g.p1.name
                : g.winner === "p2"
                ? g.p2.name
                : "SERI",
          });
        }
      }

      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animId);
      stopBgm();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  const handleTouchAction = (
    action: keyof TouchInputs,
    pressed: boolean
  ) => {
    touchInputsRef.current[action] = pressed;
  };

  return (
    <main className="tt-game">
      <div className="tt-frame">
        <header className="tt-top">
          <span className="tt-brand">
            ✦ STERIA HOUSE <b>/ TANTANGAN TERBUKA</b>
          </span>
          <div className="tt-top-right">
            <span>FAST-PACED PIXEL DUEL</span>
            <button
              className="tt-sound-btn"
              onClick={() => setBgmOn(toggleBgm())}
              aria-label="Toggle BGM"
            >
              {bgmOn ? "🎵 BGM ON" : "🎵 BGM OFF"}
            </button>
            <button
              className="tt-sound-btn"
              onClick={() => setSoundOn(toggleSound())}
              aria-label="Toggle Sound"
            >
              {soundOn ? "🔊 SFX ON" : "🔇 SFX OFF"}
            </button>
          </div>
        </header>

        <div className="tt-stage">
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            aria-label="Arena Tantangan Terbuka Pixel Duel"
          />

          {hudState && !inMenu && (
            <div className="tt-hud">
              {/* Player 1 HUD */}
              <div className="tt-fighter-hud left">
                <div className="tt-hud-header">
                  <span style={{ color: CHAR_INFO[selectedP1].theme }}>
                    {CHAR_INFO[selectedP1].name}
                  </span>
                  <div className="tt-rounds-won">
                    <span
                      className={`tt-round-pip ${
                        hudState.p1Rounds >= 1 ? "filled" : ""
                      }`}
                    />
                    <span
                      className={`tt-round-pip ${
                        hudState.p1Rounds >= 2 ? "filled" : ""
                      }`}
                    />
                  </div>
                </div>
                <div className="tt-health-bar">
                  <div
                    className="tt-chip-meter"
                    style={{
                      width: `${(hudState.p1ChipHp / hudState.p1MaxHp) * 100}%`,
                    }}
                  />
                  <div
                    className="tt-hp-meter"
                    style={{
                      width: `${(hudState.p1Hp / hudState.p1MaxHp) * 100}%`,
                    }}
                  />
                </div>
                <div className="tt-guard-bar">
                  <div
                    className={`tt-guard-meter ${
                      hudState.p1Guard < 30 ? "danger" : ""
                    }`}
                    style={{ width: `${hudState.p1Guard}%` }}
                  />
                </div>
                <div className="tt-ki-bar">
                  <div
                    className={`tt-ki-meter ${
                      hudState.p1Ki >= 100 ? "ready" : ""
                    }`}
                    style={{ width: `${hudState.p1Ki}%` }}
                  />
                </div>
              </div>

              {/* Timer & Round */}
              <div className="tt-hud-center">
                <span className="tt-timer">{hudState.timer}</span>
                <span className="tt-round-label">
                  {hudState.round === 3 ? "FINAL" : `RONDE ${hudState.round}`}
                </span>
              </div>

              {/* Player 2 HUD */}
              <div className="tt-fighter-hud right">
                <div className="tt-hud-header">
                  <span
                    style={{
                      color:
                        CHAR_INFO[
                          gameMode === "pve"
                            ? selectedP1 === "ren"
                              ? "boma"
                              : "ren"
                            : selectedP2
                        ].theme,
                    }}
                  >
                    {gameMode === "pve"
                      ? `CPU (${
                          selectedP1 === "ren"
                            ? CHAR_INFO.boma.name
                            : CHAR_INFO.ren.name
                        })`
                      : CHAR_INFO[selectedP2].name}
                  </span>
                  <div className="tt-rounds-won">
                    <span
                      className={`tt-round-pip ${
                        hudState.p2Rounds >= 1 ? "filled" : ""
                      }`}
                    />
                    <span
                      className={`tt-round-pip ${
                        hudState.p2Rounds >= 2 ? "filled" : ""
                      }`}
                    />
                  </div>
                </div>
                <div className="tt-health-bar">
                  <div
                    className="tt-chip-meter"
                    style={{
                      width: `${(hudState.p2ChipHp / hudState.p2MaxHp) * 100}%`,
                    }}
                  />
                  <div
                    className="tt-hp-meter"
                    style={{
                      width: `${(hudState.p2Hp / hudState.p2MaxHp) * 100}%`,
                    }}
                  />
                </div>
                <div className="tt-guard-bar">
                  <div
                    className={`tt-guard-meter ${
                      hudState.p2Guard < 30 ? "danger" : ""
                    }`}
                    style={{ width: `${hudState.p2Guard}%` }}
                  />
                </div>
                <div className="tt-ki-bar">
                  <div
                    className={`tt-ki-meter ${
                      hudState.p2Ki >= 100 ? "ready" : ""
                    }`}
                    style={{ width: `${hudState.p2Ki}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {hudState && hudState.p1Combo >= 2 && (
            <div className="tt-combo-p1">
              {hudState.p1Combo}x COMBO!
            </div>
          )}
          {hudState && hudState.p2Combo >= 2 && (
            <div className="tt-combo-p2">
              {hudState.p2Combo}x COMBO!
            </div>
          )}

          {hudState?.announcement.visible && !inMenu && (
            <div className="tt-announcement">
              <h2>{hudState.announcement.title}</h2>
              <p>{hudState.announcement.subtitle}</p>
            </div>
          )}

          {(inMenu || hudState?.matchOver) && (
            <div className="tt-menu-overlay">
              <div className="tt-card">
                <span className="tt-eyebrow">✦ 16-BIT PIXEL FAST-PACED DUEL ✦</span>
                <h1>
                  TANTANGAN <span>TERBUKA</span>
                </h1>
                <p className="tt-desc">
                  {hudState?.matchOver
                    ? `Pertarungan usai! ${hudState.winnerName} keluar sebagai pemenang sejati.`
                    : "Pilih pendekar andalanmu. Kuasai timing tebasan, tangkisan parry, double jump, dan kombinasi tombol kombo!"}
                </p>

                <div className="tt-options-row">
                  <button
                    className={`tt-opt-btn ${gameMode === "pve" ? "active" : ""}`}
                    onClick={() => setGameMode("pve")}
                  >
                    1P VS AI (KOMPUTER)
                  </button>
                  <button
                    className={`tt-opt-btn ${gameMode === "pvp" ? "active" : ""}`}
                    onClick={() => setGameMode("pvp")}
                  >
                    2P VERSUS (LOKAL)
                  </button>
                </div>

                {gameMode === "pve" && (
                  <div className="tt-options-row">
                    <button
                      className={`tt-opt-btn ${
                        difficulty === "easy" ? "active" : ""
                      }`}
                      onClick={() => setDifficulty("easy")}
                    >
                      LATIHAN
                    </button>
                    <button
                      className={`tt-opt-btn ${
                        difficulty === "normal" ? "active" : ""
                      }`}
                      onClick={() => setDifficulty("normal")}
                    >
                      PENDEKAR
                    </button>
                    <button
                      className={`tt-opt-btn ${
                        difficulty === "hard" ? "active" : ""
                      }`}
                      onClick={() => setDifficulty("hard")}
                    >
                      MASTER
                    </button>
                  </div>
                )}

                <div className="tt-options-row">
                  <button
                    className={`tt-opt-btn ${
                      arena === "padepokan" ? "active" : ""
                    }`}
                    onClick={() => setArena("padepokan")}
                  >
                    PADEPOKAN SENJA
                  </button>
                  <button
                    className={`tt-opt-btn ${
                      arena === "kuil-rembulan" ? "active" : ""
                    }`}
                    onClick={() => setArena("kuil-rembulan")}
                  >
                    KUIL REMBULAN
                  </button>
                </div>

                <div className="tt-roster-section">
                  <span className="tt-roster-label">PENDEKAR P1:</span>
                  <div className="tt-roster">
                    <button
                      className={`tt-char-card ${
                        selectedP1 === "ren" ? "selected" : ""
                      }`}
                      onClick={() => setSelectedP1("ren")}
                    >
                      <img
                        className="tt-char-gif"
                        src={ASSET_ROOT + "ren-idle.gif"}
                        alt="Ren"
                      />
                      <strong className="tt-char-name">REN</strong>
                      <small className="tt-char-sub">
                        Katana · Cepat & Lincah
                        <br />
                        Jurus: Tebasan Bulan Sabit
                      </small>
                    </button>

                    <button
                      className={`tt-char-card ${
                        selectedP1 === "boma" ? "selected" : ""
                      }`}
                      onClick={() => setSelectedP1("boma")}
                    >
                      <img
                        className="tt-char-gif"
                        src={ASSET_ROOT + "boma-idle.gif"}
                        alt="Boma"
                      />
                      <strong className="tt-char-name">BOMA</strong>
                      <small className="tt-char-sub">
                        Tinju Bara · Kuat & Tangguh
                        <br />
                        Jurus: Tendangan Naga Bara
                      </small>
                    </button>
                  </div>
                </div>

                {gameMode === "pvp" && (
                  <div className="tt-roster-section">
                    <span className="tt-roster-label">PENDEKAR P2:</span>
                    <div className="tt-roster">
                      <button
                        className={`tt-char-card ${
                          selectedP2 === "ren" ? "selected" : ""
                        }`}
                        onClick={() => setSelectedP2("ren")}
                      >
                        <img
                          className="tt-char-gif"
                          src={ASSET_ROOT + "ren-idle.gif"}
                          alt="Ren"
                        />
                        <strong className="tt-char-name">REN</strong>
                        <small className="tt-char-sub">
                          Katana · Cepat & Lincah
                        </small>
                      </button>

                      <button
                        className={`tt-char-card ${
                          selectedP2 === "boma" ? "selected" : ""
                        }`}
                        onClick={() => setSelectedP2("boma")}
                      >
                        <img
                          className="tt-char-gif"
                          src={ASSET_ROOT + "boma-idle.gif"}
                          alt="Boma"
                        />
                        <strong className="tt-char-name">BOMA</strong>
                        <small className="tt-char-sub">
                          Tinju Bara · Kuat & Tangguh
                        </small>
                      </button>
                    </div>
                  </div>
                )}

                {/* Combos Guide */}
                <div className="tt-combo-guide">
                  <span className="tt-combo-badge">
                    <b>W + W</b> Double Jump
                  </span>
                  <span className="tt-combo-badge">
                    <b>W + J</b> Rising Launcher (Air)
                  </span>
                  <span className="tt-combo-badge">
                    <b>S + J</b> Low Sweep (Kaki)
                  </span>
                  <span className="tt-combo-badge">
                    <b>D/A + K</b> Lunge Strike
                  </span>
                  <span className="tt-combo-badge">
                    <b>S + K</b> Ground Slam
                  </span>
                  <span className="tt-combo-badge">
                    <b>S Tahan</b> Tangkis / Parry
                  </span>
                </div>

                <button className="tt-btn-start" onClick={startMatch}>
                  <span>{hudState?.matchOver ? "TANDING ULANG" : "MULAI DUEL"}</span>
                  <span>⚔</span>
                </button>

                <small className="tt-keyboard-hints">
                  <b>P1:</b> A/D Gerak · W Lompat (2x) · S Tangkis · J Serang · K Berat · L Jurus · SHIFT Dash
                </small>
              </div>
            </div>
          )}

          {!inMenu && !hudState?.matchOver && (
            <div className="tt-touch-controls">
              <div className="tt-dpad">
                <button
                  className="tt-touch-btn"
                  onPointerDown={() => handleTouchAction("jump", true)}
                  onPointerUp={() => handleTouchAction("jump", false)}
                >
                  ▲ 2x
                </button>
                <div />
                <button
                  className="tt-touch-btn"
                  onPointerDown={() => handleTouchAction("block", true)}
                  onPointerUp={() => handleTouchAction("block", false)}
                >
                  🛡️
                </button>
                <button
                  className="tt-touch-btn"
                  onPointerDown={() => handleTouchAction("left", true)}
                  onPointerUp={() => handleTouchAction("left", false)}
                >
                  ◀
                </button>
                <button
                  className="tt-touch-btn"
                  onPointerDown={() => handleTouchAction("duck", true)}
                  onPointerUp={() => handleTouchAction("duck", false)}
                >
                  ▼
                </button>
                <button
                  className="tt-touch-btn"
                  onPointerDown={() => handleTouchAction("right", true)}
                  onPointerUp={() => handleTouchAction("right", false)}
                >
                  ▶
                </button>
              </div>

              <div>
                <div className="tt-touch-helpers">
                  <button
                    className="tt-helper-btn"
                    onPointerDown={() => handleTouchAction("launcherQuick", true)}
                    onPointerUp={() => handleTouchAction("launcherQuick", false)}
                  >
                    ▲+J LAUNCH
                  </button>
                  <button
                    className="tt-helper-btn"
                    onPointerDown={() => handleTouchAction("sweepQuick", true)}
                    onPointerUp={() => handleTouchAction("sweepQuick", false)}
                  >
                    ▼+J SWEEP
                  </button>
                </div>
                <div className="tt-action-buttons">
                  <button
                    className="tt-act-btn dash"
                    onPointerDown={() => handleTouchAction("dash", true)}
                    onPointerUp={() => handleTouchAction("dash", false)}
                  >
                    <span>DASH</span>
                    <small>💨</small>
                  </button>
                  <button
                    className="tt-act-btn attack"
                    onPointerDown={() => handleTouchAction("light", true)}
                    onPointerUp={() => handleTouchAction("light", false)}
                  >
                    <span>SERANG</span>
                    <small>⚔</small>
                  </button>
                  <button
                    className="tt-act-btn heavy"
                    onPointerDown={() => handleTouchAction("heavy", true)}
                    onPointerUp={() => handleTouchAction("heavy", false)}
                  >
                    <span>BERAT</span>
                    <small>💥</small>
                  </button>
                  <button
                    className="tt-act-btn special"
                    disabled={hudState ? hudState.p1Ki < 100 : true}
                    onPointerDown={() => handleTouchAction("special", true)}
                    onPointerUp={() => handleTouchAction("special", false)}
                  >
                    <span>JURUS</span>
                    <small>✦</small>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="tt-bottom">
          <span>
            P1: <b>W (2x)</b> Double Jump · <b>W+J</b> Launcher · <b>S+J</b> Sweep · <b>D+K</b> Lunge · <b>S</b> Tangkis
          </span>
          <span>100% PROGRAMMATIC AUDIO & CC0 ASSETS</span>
        </footer>
      </div>

      <div className="tt-rotate-notice">
        <span className="tt-rotate-icon">↻</span>
        <strong>Putar Layar ke Landscape</strong>
        <span>Tantangan Terbuka dirancang untuk layar horizontal.</span>
      </div>
    </main>
  );
}
